import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  AddRounded,
  ArrowForwardRounded,
  AutoAwesomeRounded,
  BalanceRounded,
  CheckCircleRounded,
  CloudUploadRounded,
  DescriptionRounded,
  DownloadRounded,
  EventRounded,
  FactCheckRounded,
  FolderSpecialRounded,
  GavelRounded,
  HistoryRounded,
  LockRounded,
  MicRounded,
  SearchRounded,
  TaskAltRounded,
  WarningAmberRounded,
  AccessTimeRounded,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  analyzeEvidence,
  approveRecord,
  createMatter,
  createMatterRecord,
  evidenceDownloadUrl,
  getAudit,
  getMatter,
  listMatters,
  transcribeEvidence,
  updateFact,
  updateIssue,
  updateMatter,
  updateTask,
  uploadEvidence,
} from '../features/matters/api';
import { createTimeEntry } from '../features/account/api';
import { ACTIVE_MATTER_KEY, COPILOT_PROFILE_KEY, COPILOT_PREFILL_KEY, matterLanguage } from '../edition';

const VIEW_TABS = { overview: 0, evidence: 1, record: 2, issues: 3, workflow: 4, audit: 5 };
const TAB_VIEWS = Object.fromEntries(Object.entries(VIEW_TABS).map(([view, index]) => [index, view]));

const EMPTY_MATTER = {
  title: '',
  clientName: '',
  matterType: 'civil',
  practiceArea: 'Civil dispute',
  jurisdiction: 'Ontario',
  court: '',
  objectives: '',
  fileNumber: '',
  nextCourtDate: '',
};

const RECORD_DEFAULTS = {
  fact: { statement: '', status: 'reported', sourceEvidenceId: '', sourceLocator: '', disputed: false, notes: '' },
  timeline: { title: '', eventDate: '', description: '', status: 'reported', sourceEvidenceId: '', sourceLocator: '' },
  issue: { title: '', question: '', priority: 'normal', governingLaw: '' },
  task: { title: '', priority: 'normal', dueDate: '', requiresApproval: false },
};

const newTimeForm = () => ({
  workDate: new Date().toISOString().slice(0, 10),
  hours: '0.5',
  description: '',
  billable: true,
});

function formatDate(value, includeTime = false) {
  if (!value) return 'Not set';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-CA', includeTime
    ? { dateStyle: 'medium', timeStyle: 'short' }
    : { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 ** 2)).toFixed(1)} MB`;
}

function toneFor(value) {
  const text = String(value || '').toLowerCase();
  if (/urgent|failed|disputed|rejected/.test(text)) return 'error';
  if (/high|review|warning|reported|processing/.test(text)) return 'warning';
  if (/complete|approved|documented|preserved|active/.test(text)) return 'success';
  return 'default';
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
      <Box>
        <Typography variant="h5">{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Stack>
  );
}

function EmptyState({ icon, title, body, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'transparent' }}>
      <Box sx={{ color: 'text.disabled', '& svg': { fontSize: 40 } }}>{icon}</Box>
      <Typography variant="h6" sx={{ mt: 1 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: action ? 2 : 0 }}>{body}</Typography>
      {action}
    </Paper>
  );
}

function Metric({ label, value, icon, color = '#245B88' }) {
  return (
    <Card sx={{ p: 1.8 }}>
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: `${color}14`, color }}>{icon}</Box>
        <Box><Typography sx={{ fontSize: 20, fontWeight: 780, lineHeight: 1.1 }}>{value}</Typography><Typography variant="caption" color="text.secondary">{label}</Typography></Box>
      </Stack>
    </Card>
  );
}

export default function MatterWorkspace() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const uploadRef = useRef(null);
  const [matters, setMatters] = useState([]);
  const [matterQuery, setMatterQuery] = useState('');
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(() => VIEW_TABS[searchParams.get('view')] ?? 0);
  const [snackbar, setSnackbar] = useState('');
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newMatter, setNewMatter] = useState(EMPTY_MATTER);
  const [overviewDraft, setOverviewDraft] = useState(EMPTY_MATTER);
  const [recordKind, setRecordKind] = useState('');
  const [recordForm, setRecordForm] = useState({});
  const [selectedEvidence, setSelectedEvidence] = useState([]);
  const [processing, setProcessing] = useState(null);
  const [processingConsent, setProcessingConsent] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditIntegrity, setAuditIntegrity] = useState(null);
  const [issueReview, setIssueReview] = useState(null);
  const [taskStage, setTaskStage] = useState('');
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeForm, setTimeForm] = useState(newTimeForm);

  const refreshMatter = useCallback(async matterId => {
    const body = await getMatter(matterId);
    setMatter(body.matter);
    setOverviewDraft({
      title: body.matter.title,
      clientName: body.matter.clientName,
      matterType: body.matter.matterType,
      practiceArea: body.matter.practiceArea,
      jurisdiction: body.matter.jurisdiction,
      court: body.matter.court,
      objectives: body.matter.objectives,
      fileNumber: body.matter.fileNumber,
      nextCourtDate: body.matter.nextCourtDate || '',
      stage: body.matter.stage,
      status: body.matter.status,
      assignedTo: body.matter.assignedTo,
    });
    setTaskStage(current => current || body.matter.stage || body.matter.tasks[0]?.stage || '');
    localStorage.setItem(ACTIVE_MATTER_KEY, body.matter.id);
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('id', body.matter.id);
      next.delete('matterId');
      return next;
    }, { replace: true });
    return body.matter;
  }, [setSearchParams]);

  const loadWorkspace = useCallback(async preferredId => {
    setLoading(true);
    setError('');
    try {
      const body = await listMatters();
      setMatters(body.matters || []);
      const requested = preferredId || searchParams.get('id') || localStorage.getItem(ACTIVE_MATTER_KEY);
      const target = (body.matters || []).find(item => item.id === requested) || body.matters?.[0];
      if (target) await refreshMatter(target.id);
      else setMatter(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [refreshMatter, searchParams]);

  useEffect(() => {
    loadWorkspace();
    // The initial URL selects the matter; subsequent changes are handled directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      listMatters(matterQuery).then(body => setMatters(body.matters || [])).catch(err => setError(err.message));
    }, 250);
    return () => clearTimeout(timer);
  }, [matterQuery]);

  useEffect(() => {
    if (tab !== 5 || !matter) return;
    getAudit(matter.id).then(body => {
      setAudit(body.events || []);
      setAuditIntegrity(body.integrity || null);
    }).catch(err => setError(err.message));
  }, [tab, matter]);

  const counts = useMemo(() => {
    if (!matter) return { evidence: 0, facts: 0, issues: 0, completed: 0, tasks: 0, progress: 0 };
    const completed = matter.tasks.filter(task => task.status === 'completed').length;
    return {
      evidence: matter.evidence.length,
      facts: matter.facts.length,
      issues: matter.issues.filter(issue => issue.status !== 'closed').length,
      completed,
      tasks: matter.tasks.length,
      progress: matter.tasks.length ? Math.round((completed / matter.tasks.length) * 100) : 0,
    };
  }, [matter]);

  const stages = useMemo(() => {
    const unique = new Map();
    for (const task of matter?.tasks || []) unique.set(task.stage, task.stageTitle || task.stage);
    return [...unique.entries()].map(([id, title]) => ({ id, title }));
  }, [matter]);
  const matterOptions = useMemo(() => (
    matter && !matters.some(item => item.id === matter.id) ? [matter, ...matters] : matters
  ), [matter, matters]);

  async function handleCreateMatter() {
    if (!newMatter.title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const body = await createMatter(newMatter);
      setCreateOpen(false);
      setNewMatter(EMPTY_MATTER);
      await loadWorkspace(body.matter.id);
      setSnackbar(`${body.matter.matterNumber} created with the full lifecycle playbook.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveOverview() {
    if (!matter) return;
    setSaving(true);
    try {
      const body = await updateMatter(matter.id, overviewDraft);
      setMatter(body.matter);
      setMatters(current => current.map(item => item.id === body.matter.id ? { ...item, ...body.matter } : item));
      setSnackbar('Matter details saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload(event) {
    const files = [...(event.target.files || [])];
    event.target.value = '';
    if (!files.length || !matter) return;
    setSaving(true);
    try {
      const uploaded = [];
      for (const file of files) uploaded.push((await uploadEvidence(matter.id, file)).evidence);
      await refreshMatter(matter.id);
      setSelectedEvidence(uploaded.map(item => item.id));
      setSnackbar(`${uploaded.length} ${matterLanguage(matter).evidence} file${uploaded.length === 1 ? '' : 's'} encrypted, fingerprinted, and selected for review.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openRecord(kind) {
    setRecordKind(kind);
    setRecordForm({ ...RECORD_DEFAULTS[kind] });
  }

  async function handleCreateRecord() {
    if (!matter || !recordKind) return;
    const collection = recordKind === 'fact' ? 'facts' : recordKind === 'timeline' ? 'timeline' : recordKind === 'issue' ? 'issues' : 'tasks';
    setSaving(true);
    try {
      await createMatterRecord(matter.id, collection, recordForm);
      setRecordKind('');
      await refreshMatter(matter.id);
      setSnackbar(`${recordKind[0].toUpperCase()}${recordKind.slice(1)} added to the matter record.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTaskStatus(task, checked) {
    try {
      await updateTask(matter.id, task.id, { status: checked ? 'completed' : 'not-started' });
      await refreshMatter(matter.id);
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleApprove(task) {
    try {
      await approveRecord(matter.id, { entityType: 'workflow_task', entityId: task.id, decision: 'approved', note: 'Approved in the matter workspace.' });
      await refreshMatter(matter.id);
      setSnackbar('Approval recorded in the audit trail.');
    } catch (err) {
      setError(err.message);
    }
  }

  function openAiProcessing(kind, evidenceId = '', evidenceIds = null) {
    setProcessingConsent(false);
    setProcessing({ kind, evidenceId, evidenceIds });
  }

  async function handleAiProcessing() {
    if (!processing || !processingConsent || !matter) return;
    setSaving(true);
    try {
      if (processing.kind === 'analyze') {
        const reviewEvidenceIds = processing.evidenceIds || selectedEvidence;
        const result = await analyzeEvidence(matter.id, reviewEvidenceIds);
        const total = Object.values(result.created || {}).reduce((sum, value) => sum + value, 0);
        setSnackbar(`Disclosure review completed with ${total} source-linked matter records and a draft research memorandum.`);
        setSelectedEvidence([]);
      } else {
        await transcribeEvidence(matter.id, processing.evidenceId);
        setSnackbar('Transcript created as a separate, versioned derivative. The original is unchanged.');
      }
      setProcessing(null);
      await refreshMatter(matter.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleIssueReview(value) {
    if (!matter || !issueReview) return;
    setSaving(true);
    try {
      await updateIssue(matter.id, issueReview.id, value);
      await refreshMatter(matter.id);
      setIssueReview(null);
      setSnackbar('Investigation outcome and resolution basis recorded in the tamper-evident audit trail.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function openCopilot(issue) {
    const profile = {
      matterId: matter.id,
      matterName: matter.title,
      matterType: matter.matterType,
      clientName: matter.clientName,
      jurisdiction: matter.jurisdiction,
      court: matter.court,
      currentStage: matter.stage,
      charges: (matter.intake?.charges || []).map(charge => `${charge.charge || ''} ${charge.statute || ''}`.trim()).filter(Boolean),
      claims: matter.intake?.claims || [],
      parties: (matter.intake?.parties || []).map(party => `${party.name || ''} (${party.role || 'party'})`.trim()),
      remedies: matter.intake?.remedies || [],
      objectives: matter.objectives,
      keyFacts: matter.facts.map(fact => fact.statement).join('\n'),
      nextCourtDate: matter.nextCourtDate || '',
      lawyerPreferences: 'Separate verified facts, disputed facts, assumptions, supporting authorities, adverse authorities, and required human decisions.',
    };
    localStorage.setItem(ACTIVE_MATTER_KEY, matter.id);
    localStorage.setItem(COPILOT_PROFILE_KEY, JSON.stringify(profile));
    if (issue) localStorage.setItem(COPILOT_PREFILL_KEY, `Research this issue for ${matter.title}: ${issue.title}. ${issue.question}`);
    navigate('/copilot');
  }

  async function handleLogTime() {
    if (!matter) return;
    const minutes = Math.round(Number(timeForm.hours) * 60);
    if (!Number.isFinite(minutes) || minutes < 1 || !timeForm.description.trim()) return;
    setSaving(true);
    try {
      await createTimeEntry({
        matterId: matter.id,
        workDate: timeForm.workDate,
        minutes,
        description: timeForm.description,
        activity: 'Legal work',
        billable: timeForm.billable,
      });
      setTimeOpen(false);
      setTimeForm(newTimeForm());
      setSnackbar('Time recorded for this matter.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ height: '65vh', display: 'grid', placeItems: 'center' }}><Stack alignItems="center" spacing={1.5}><CircularProgress /><Typography color="text.secondary">Opening the secure matter workspace…</Typography></Stack></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 5 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="overline" color="secondary.main">Private case file</Typography>
            <Chip size="small" icon={<LockRounded />} label="Separated from public case law" variant="outlined" color="success" />
          </Stack>
          <Typography variant="h3">Matter workspace</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.55 }}>Evidence, facts, issues, research, workflow, approvals, and audit history in one file.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField size="small" value={matterQuery} onChange={event => setMatterQuery(event.target.value)} placeholder="Find a matter" InputProps={{ startAdornment: <SearchRounded sx={{ mr: 0.8, fontSize: 19, color: 'text.disabled' }} /> }} sx={{ width: { xs: 150, sm: 210 }, bgcolor: 'background.paper' }} />
          {matterOptions.length > 0 && (
            <Select
              size="small"
              value={matter?.id || ''}
              onChange={event => refreshMatter(event.target.value).catch(err => setError(err.message))}
              sx={{ minWidth: { xs: 190, sm: 280 }, bgcolor: 'background.paper' }}
            >
              {matterOptions.map(item => <MenuItem key={item.id} value={item.id}>{item.matterNumber} · {item.title}</MenuItem>)}
            </Select>
          )}
          <Button variant="contained" startIcon={<AddRounded />} onClick={() => setCreateOpen(true)}>New matter</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {!matter ? (
        <EmptyState
          icon={<FolderSpecialRounded />}
          title="Create the first private matter"
          body="Every matter follows the same guided journey with a private evidence vault, source-linked facts, approvals, and an audit trail."
          action={<Button variant="contained" startIcon={<AddRounded />} onClick={() => setCreateOpen(true)}>Create matter</Button>}
        />
      ) : (
        <>
          <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
              <Box sx={{ width: 52, height: 52, borderRadius: 3, display: 'grid', placeItems: 'center', bgcolor: theme => alpha(theme.palette.primary.main, 0.09), color: 'primary.main' }}><GavelRounded /></Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h4">{matter.title}</Typography>
                  <Chip size="small" label={matter.status} color={toneFor(matter.status)} />
                  <Chip size="small" label={matter.matterNumber} variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>{matter.clientName || 'Client not yet recorded'} · {matter.jurisdiction || 'Jurisdiction not set'} · {matter.court || 'Court not set'}</Typography>
              </Box>
              <Box sx={{ minWidth: 210 }}>
                <Stack direction="row" justifyContent="space-between"><Typography variant="caption">Lifecycle completion</Typography><Typography variant="caption" fontWeight={800}>{counts.progress}%</Typography></Stack>
                <LinearProgress variant="determinate" value={counts.progress} sx={{ mt: 0.75, height: 7, borderRadius: 9 }} />
              </Box>
              <Stack direction="row" spacing={1}><Button variant="outlined" startIcon={<AccessTimeRounded />} onClick={() => { setTimeForm(newTimeForm()); setTimeOpen(true); }}>Log time</Button><Button variant="outlined" startIcon={<AutoAwesomeRounded />} onClick={() => openCopilot()}>Open copilot</Button></Stack>
            </Stack>
          </Card>

          {matter.intake?.scopeAssessment && <Alert severity={matter.intake.scopeAssessment.canOpenForRepresentation ? 'success' : 'warning'} sx={{ mb: 2 }}><strong>{matter.intake.scopeAssessment.label}.</strong> {matter.intake.scopeAssessment.reasons?.join(' ')} {!matter.intake.scopeAssessment.canOpenForRepresentation && matter.intake.scopeAssessment.requiredActions?.[0]}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.4, mb: 2 }}>
            <Metric label="Evidence files" value={counts.evidence} icon={<DescriptionRounded />} />
            <Metric label="Matter facts" value={counts.facts} icon={<FactCheckRounded />} color="#0D9488" />
            <Metric label="Open issues" value={counts.issues} icon={<BalanceRounded />} color="#D97706" />
            <Metric label="Tasks complete" value={`${counts.completed}/${counts.tasks}`} icon={<TaskAltRounded />} color="#6D4FB3" />
          </Box>

          <Card sx={{ overflow: 'hidden' }}>
            <Tabs value={tab} onChange={(_, value) => {
              setTab(value);
              setSearchParams(current => {
                const next = new URLSearchParams(current);
                next.set('view', TAB_VIEWS[value] || 'overview');
                return next;
              }, { replace: true });
            }} variant="scrollable" scrollButtons="auto" sx={{ px: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Tab label="Overview" />
              <Tab label={`Evidence (${counts.evidence})`} />
              <Tab label={`Facts & timeline (${counts.facts + matter.timeline.length})`} />
              <Tab label={`Issues & research (${counts.issues})`} />
              <Tab label={`Workflow (${counts.tasks})`} />
              <Tab label="Audit" />
            </Tabs>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              {tab === 0 && (
                <OverviewPanel
                  matter={matter}
                  draft={overviewDraft}
                  setDraft={setOverviewDraft}
                  onSave={handleSaveOverview}
                  saving={saving}
                />
              )}
              {tab === 1 && (
                <EvidencePanel
                  matter={matter}
                  selected={selectedEvidence}
                  setSelected={setSelectedEvidence}
                  onUpload={() => uploadRef.current?.click()}
                  onAnalyze={evidenceIds => openAiProcessing('analyze', '', evidenceIds)}
                  onTranscribe={evidenceId => openAiProcessing('transcribe', evidenceId)}
                />
              )}
              {tab === 2 && (
                <FactsTimelinePanel
                  matter={matter}
                  onAddFact={() => openRecord('fact')}
                  onAddEvent={() => openRecord('timeline')}
                  onToggleDisputed={async fact => {
                    await updateFact(matter.id, fact.id, { disputed: !fact.disputed });
                    await refreshMatter(matter.id);
                  }}
                />
              )}
              {tab === 3 && <IssuesPanel matter={matter} onAdd={() => openRecord('issue')} onResearch={openCopilot} onRecordOutcome={setIssueReview} />}
              {tab === 4 && (
                <WorkflowPanel
                  matter={matter}
                  stages={stages}
                  taskStage={taskStage}
                  setTaskStage={setTaskStage}
                  onAdd={() => openRecord('task')}
                  onStatus={handleTaskStatus}
                  onApprove={handleApprove}
                />
              )}
              {tab === 5 && <AuditPanel audit={audit} integrity={auditIntegrity} />}
            </Box>
          </Card>
        </>
      )}

      <input ref={uploadRef} type="file" multiple hidden onChange={handleUpload} />

      <CreateMatterDialog open={createOpen} value={newMatter} setValue={setNewMatter} onClose={() => setCreateOpen(false)} onCreate={handleCreateMatter} saving={saving} />
      <RecordDialog kind={recordKind} value={recordForm} setValue={setRecordForm} matter={matter} onClose={() => setRecordKind('')} onCreate={handleCreateRecord} saving={saving} />
      <ProcessingDialog
        processing={processing}
        selectedCount={processing?.evidenceIds?.length || selectedEvidence.length}
        consent={processingConsent}
        setConsent={setProcessingConsent}
        onClose={() => setProcessing(null)}
        onRun={handleAiProcessing}
        saving={saving}
        matter={matter}
      />
      <IssueResolutionDialog issue={issueReview} onClose={() => setIssueReview(null)} onSave={handleIssueReview} saving={saving} />
      <Dialog open={timeOpen} onClose={() => setTimeOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Log time · {matter?.matterNumber}</DialogTitle>
        <DialogContent dividers><Stack spacing={2} sx={{ pt: 0.5 }}><Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}><TextField label="Date" type="date" InputLabelProps={{ shrink: true }} value={timeForm.workDate} onChange={event => setTimeForm(current => ({ ...current, workDate: event.target.value }))} /><TextField label="Hours" type="number" inputProps={{ min: 0.1, max: 24, step: 0.1 }} value={timeForm.hours} onChange={event => setTimeForm(current => ({ ...current, hours: event.target.value }))} /></Box><TextField label="Work completed" value={timeForm.description} onChange={event => setTimeForm(current => ({ ...current, description: event.target.value }))} multiline minRows={3} required autoFocus /><FormControlLabel control={<Checkbox checked={timeForm.billable} onChange={event => setTimeForm(current => ({ ...current, billable: event.target.checked }))} />} label="Billable" /></Stack></DialogContent>
        <DialogActions><Button onClick={() => setTimeOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleLogTime} disabled={saving || !timeForm.description.trim() || Number(timeForm.hours) <= 0}>{saving ? 'Saving…' : 'Save time'}</Button></DialogActions>
      </Dialog>
      <Snackbar open={Boolean(snackbar)} autoHideDuration={4500} onClose={() => setSnackbar('')} message={snackbar} />
    </Box>
  );
}

function OverviewPanel({ matter, draft, setDraft, onSave, saving }) {
  const field = key => event => setDraft(current => ({ ...current, [key]: event.target.value }));
  return (
    <Box>
      <SectionHeader title="Matter profile" subtitle="The verified context shared with workflow tools and the personalized copilot." />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
        <TextField label="Matter title" value={draft.title || ''} onChange={field('title')} fullWidth />
        <TextField label="Client name" value={draft.clientName || ''} onChange={field('clientName')} fullWidth />
        <TextField label="Practice area" value={draft.practiceArea || ''} onChange={field('practiceArea')} fullWidth />
        <TextField label="Jurisdiction" value={draft.jurisdiction || ''} onChange={field('jurisdiction')} fullWidth />
        <TextField label="Court / tribunal" value={draft.court || ''} onChange={field('court')} fullWidth />
        <TextField label="Court or police file number" value={draft.fileNumber || ''} onChange={field('fileNumber')} fullWidth />
        <TextField label="Next court date" type="date" value={draft.nextCourtDate || ''} onChange={field('nextCourtDate')} InputLabelProps={{ shrink: true }} fullWidth />
        <TextField label="Assigned professional" value={draft.assignedTo || ''} onChange={field('assignedTo')} fullWidth />
        <TextField label="Current lifecycle stage" select value={draft.stage || 'intake-retainer'} onChange={field('stage')} fullWidth>
          {[...new Map(matter.tasks.map(task => [task.stage, task.stageTitle])).entries()].map(([stage, title]) => <MenuItem key={stage} value={stage}>{title || stage}</MenuItem>)}
        </TextField>
        <TextField label="Status" select value={draft.status || 'active'} onChange={field('status')} fullWidth>
          {['active', 'intake', 'on-hold', 'closed'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}
        </TextField>
        <TextField label="Client objectives and instructions" value={draft.objectives || ''} onChange={field('objectives')} multiline minRows={4} fullWidth sx={{ gridColumn: { md: '1 / -1' } }} />
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mt: 2.5 }}>
        <Alert severity="info" icon={<LockRounded />} sx={{ flex: 1 }}>Private matter information is stored separately from the public judgment index. The app never adds client evidence to shared case-law search.</Alert>
        <Button variant="contained" onClick={onSave} disabled={saving || !draft.title?.trim()}>{saving ? 'Saving…' : 'Save matter'}</Button>
      </Stack>
    </Box>
  );
}

function EvidencePanel({ matter, selected, setSelected, onUpload, onAnalyze, onTranscribe }) {
  const toggle = evidenceId => setSelected(current => current.includes(evidenceId) ? current.filter(id => id !== evidenceId) : [...current, evidenceId]);
  const latestReview = matter.disclosureReviews?.[0];
  const language = matterLanguage(matter);
  return (
    <Box>
      <SectionHeader
        title="Evidence review"
        subtitle="Upload the available record, then build a searchable, source-linked review without altering the originals."
        action={<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap><Button variant="outlined" startIcon={<AutoAwesomeRounded />} disabled={!selected.length} onClick={() => onAnalyze(selected)}>Review selected ({selected.length})</Button><Button variant="outlined" startIcon={<FactCheckRounded />} disabled={!matter.evidence.length} onClick={() => onAnalyze(matter.evidence.map(item => item.id))}>Review all {matter.evidence.length}</Button><Button variant="contained" startIcon={<CloudUploadRounded />} onClick={onUpload}>Upload files</Button></Stack>}
      />
      {latestReview && <DisclosureReviewResults reviewRecord={latestReview} matter={matter} />}
      {!matter.evidence.length ? (
        <EmptyState icon={<DescriptionRounded />} title={`No ${language.evidence} uploaded yet`} body="Select the complete available record. Every file is encrypted at rest, fingerprinted, and preserved separately from generated review work." action={<Button variant="contained" startIcon={<CloudUploadRounded />} onClick={onUpload}>Upload files</Button>} />
      ) : (
        <Box sx={{ mt: latestReview ? 3 : 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 1.25 }}>
            <Box><Typography variant="h6">Preserved source files</Typography><Typography variant="caption" color="text.secondary">AES-256-GCM encrypted · SHA-256 fingerprinted · originals downloadable on permission check</Typography></Box>
            <Button size="small" onClick={() => setSelected(selected.length === matter.evidence.length ? [] : matter.evidence.map(item => item.id))}>{selected.length === matter.evidence.length ? 'Clear selection' : 'Select all'}</Button>
          </Stack>
          <Stack spacing={1.1}>
          {matter.evidence.map(item => {
            const transcript = matter.derivatives?.find(derivative => derivative.evidenceId === item.id && derivative.type === 'transcript');
            const audio = /^audio\//i.test(item.mimeType) || /^video\//i.test(item.mimeType);
            return (
              <Paper key={item.id} variant="outlined" sx={{ p: 1.6 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.4}>
                  <Checkbox checked={selected.includes(item.id)} onChange={() => toggle(item.id)} inputProps={{ 'aria-label': `Select ${item.originalName}` }} />
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'grey.50', color: 'primary.light' }}>{audio ? <MicRounded /> : <DescriptionRounded />}</Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 730 }} noWrap>{item.originalName}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatBytes(item.sizeBytes)} · SHA-256 {item.sha256.slice(0, 12)}… · preserved {formatDate(item.createdAt, true)}</Typography>
                    {transcript && <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>Transcript available · {transcript.content.length.toLocaleString()} characters · {transcript.model}</Typography>}
                  </Box>
                  <Stack direction="row" spacing={0.7} alignItems="center">
                    <Chip size="small" label={item.status} color={toneFor(item.status)} variant="outlined" />
                    <Chip size="small" label={item.aiStatus} color={toneFor(item.aiStatus)} variant="outlined" />
                    {audio && !transcript && <Tooltip title="Create a separate transcript with explicit consent"><IconButton onClick={() => onTranscribe(item.id)}><MicRounded /></IconButton></Tooltip>}
                    <Tooltip title="Download preserved original"><IconButton component="a" href={evidenceDownloadUrl(matter.id, item.id)}><DownloadRounded /></IconButton></Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

function DisclosureReviewResults({ reviewRecord, matter }) {
  const [query, setQuery] = useState('');
  const review = reviewRecord.review || {};
  const normalized = query.trim().toLowerCase();
  const matches = value => !normalized || JSON.stringify(value || '').toLowerCase().includes(normalized);
  const documents = (review.documentRegister || []).filter(matches);
  const witnesses = (review.witnesses || []).filter(matches);
  const contradictions = (review.contradictions || []).filter(matches);
  const missing = (review.missingMaterials || []).filter(matches);
  const issues = (review.issues || []).filter(matches);
  const checklist = (review.defenceChecklist || []).filter(matches);
  const sourceName = evidenceId => matter.evidence.find(item => item.id === evidenceId)?.originalName || evidenceId || 'Source not identified';
  const sourceRef = source => `${sourceName(source?.evidenceId)} · ${source?.locator || 'locator requires review'}`;

  return (
    <Card variant="outlined" sx={{ p: { xs: 1.6, md: 2.2 }, bgcolor: theme => alpha(theme.palette.secondary.main, 0.025), borderColor: 'secondary.light' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Box>
          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap><Typography variant="h5">Latest structured review</Typography><Chip size="small" label="Professional review required" color="warning" /><Chip size="small" variant="outlined" label={`${reviewRecord.evidenceIds?.length || 0} source files`} /></Stack>
          <Typography variant="caption" color="text.secondary">Created {formatDate(reviewRecord.createdAt, true)} · snapshot {reviewRecord.contentHash?.slice(0, 12)}… · {reviewRecord.model}</Typography>
        </Box>
        <TextField size="small" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search this case review" InputProps={{ startAdornment: <SearchRounded sx={{ mr: 1, fontSize: 19, color: 'text.disabled' }} /> }} sx={{ minWidth: { md: 280 }, bgcolor: 'background.paper' }} />
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1, my: 2 }}>
        {[
          ['Documents', review.documentRegister?.length || 0, '#245B88'],
          ['Witnesses', review.witnesses?.length || 0, '#0D9488'],
          ['Contradictions', review.contradictions?.length || 0, '#C2414B'],
          ['Missing items', review.missingMaterials?.length || 0, '#D97706'],
          ['Legal issues', review.issues?.length || 0, '#6D4FB3'],
        ].map(([label, value, color]) => <Paper key={label} variant="outlined" sx={{ p: 1.2 }}><Typography sx={{ color, fontSize: 20, fontWeight: 800 }}>{value}</Typography><Typography variant="caption" color="text.secondary">{label}</Typography></Paper>)}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
        <ReviewSection title="Document register" count={documents.length} empty="No matching documents">
          {documents.map((item, index) => <ReviewItem key={`${item.evidenceId}-${index}`} title={item.title || item.documentType} meta={`${sourceName(item.evidenceId)} · ${item.sourceLocator || 'locator requires review'}`} body={item.summary} chips={[item.documentType, item.documentDate, item.quality]} />)}
        </ReviewSection>
        <ReviewSection title="Witness register" count={witnesses.length} empty="No matching witnesses">
          {witnesses.map((item, index) => <ReviewItem key={`${item.name}-${index}`} title={item.name} meta={item.role} body={(item.followUp || []).join(' · ')} chips={[`${item.statements?.length || 0} sourced statements`, ...(item.credibilityFlags || []).slice(0, 1)]} />)}
        </ReviewSection>
        <ReviewSection title="Contradictions to investigate" count={contradictions.length} empty="No matching contradictions">
          {contradictions.map((item, index) => <ReviewItem key={`${item.title}-${index}`} title={item.title} meta={`${sourceRef(item.sourceA)} ↔ ${sourceRef(item.sourceB)}`} body={`${item.description} Next: ${item.investigationStep}`} chips={[item.severity]} tone="error" />)}
        </ReviewSection>
        <ReviewSection title="Missing disclosure" count={missing.length} empty="No matching missing-material flags">
          {missing.map((item, index) => <ReviewItem key={`${item.item}-${index}`} title={item.item} meta={`${sourceName(item.basisEvidenceId)} · ${item.basisLocator || 'basis locator requires review'}`} body={`${item.reason} Request: ${item.requestStep}`} chips={[item.priority]} tone="warning" />)}
        </ReviewSection>
        <ReviewSection title="Legal issues and authorities" count={issues.length} empty="No matching legal issues" wide>
          {issues.map((item, index) => {
            const authorities = [...(item.supportingAuthorities || []), ...(item.adverseAuthorities || [])];
            return <ReviewItem key={`${item.title}-${index}`} title={item.title} meta={item.governingLaw} body={item.question} chips={[item.priority, `${item.supportingAuthorities?.length || 0} supporting`, `${item.adverseAuthorities?.length || 0} adverse`]} extra={authorities.length ? <Stack spacing={0.5} sx={{ mt: 1 }}>{authorities.map((authority, authorityIndex) => <Typography key={`${authority.citation}-${authorityIndex}`} component={authority.url ? 'a' : 'span'} href={authority.url || undefined} target={authority.url ? '_blank' : undefined} rel={authority.url ? 'noreferrer' : undefined} variant="caption" sx={{ color: authority.treatment === 'adverse' ? 'error.main' : 'primary.main', textDecoration: authority.url ? 'underline' : 'none' }}>{authority.name} · {authority.citation} · {authority.pinpoint} · {authority.treatment}</Typography>)}</Stack> : null} />;
          })}
        </ReviewSection>
        <ReviewSection title="Defence preparation checklist" count={checklist.length} empty="No matching checklist items">
          {checklist.map((item, index) => <ReviewItem key={`${item.task}-${index}`} title={item.task} meta={item.issueTitle} body={item.rationale} chips={[item.priority, item.requiresApproval ? 'approval required' : 'review required']} />)}
        </ReviewSection>
        <ReviewSection title="Draft research memorandum" count={review.researchMemorandum ? 1 : 0} empty="No memorandum generated">
          {review.researchMemorandum && <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 420, overflow: 'auto', bgcolor: 'background.paper' }}><Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{review.researchMemorandum}</Typography></Paper>}
        </ReviewSection>
      </Box>
      {(review.warnings || []).length > 0 && <Alert severity="warning" sx={{ mt: 2 }}>{review.warnings.join(' · ')}</Alert>}
    </Card>
  );
}

function ReviewSection({ title, count, empty, children, wide = false }) {
  return <Box sx={{ gridColumn: wide ? { xl: '1 / -1' } : 'auto' }}><Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}><Typography sx={{ fontWeight: 780 }}>{title}</Typography><Chip size="small" label={count} variant="outlined" /></Stack><Stack spacing={0.8}>{count ? children : <Typography variant="caption" color="text.secondary">{empty}</Typography>}</Stack></Box>;
}

function ReviewItem({ title, meta, body, chips = [], tone = 'default', extra = null }) {
  return <Paper variant="outlined" sx={{ p: 1.35, borderColor: tone === 'error' ? 'error.light' : tone === 'warning' ? 'warning.light' : 'divider' }}><Typography sx={{ fontSize: 13.5, fontWeight: 740 }}>{title}</Typography>{meta && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>{meta}</Typography>}{body && <Typography variant="body2" sx={{ mt: 0.7 }}>{body}</Typography>}<Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>{chips.filter(Boolean).map((chip, index) => <Chip key={`${chip}-${index}`} size="small" label={chip} color={tone === 'error' ? 'error' : tone === 'warning' ? 'warning' : 'default'} variant="outlined" />)}</Stack>{extra}</Paper>;
}

function SourceLine({ item }) {
  return (
    <Typography variant="caption" color={item.sourceEvidenceId ? 'text.secondary' : 'warning.main'}>
      {item.sourceName || 'No evidence file linked'}{item.sourceLocator ? ` · ${item.sourceLocator}` : ' · locator missing'}
    </Typography>
  );
}

function FactsTimelinePanel({ matter, onAddFact, onAddEvent, onToggleDisputed }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 3 }}>
      <Box>
        <SectionHeader title="Fact matrix" subtitle="Conflicting versions remain visible until a human resolves them." action={<Button size="small" startIcon={<AddRounded />} onClick={onAddFact}>Add fact</Button>} />
        {!matter.facts.length ? <EmptyState icon={<FactCheckRounded />} title="No facts recorded" body="Add each material fact with its evidence source and exact locator." /> : (
          <Stack spacing={1}>
            {matter.facts.map(fact => (
              <Paper key={fact.id} variant="outlined" sx={{ p: 1.6, borderColor: fact.disputed ? 'error.light' : 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Checkbox checked={fact.disputed} onChange={() => onToggleDisputed(fact).catch(() => {})} color="error" inputProps={{ 'aria-label': `Mark disputed: ${fact.statement}` }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 680 }}>{fact.statement}</Typography>
                    <SourceLine item={fact} />
                    <Stack direction="row" spacing={0.7} sx={{ mt: 1 }}><Chip size="small" label={fact.status} color={toneFor(fact.status)} />{fact.disputed && <Chip size="small" label="Disputed" color="error" />}{fact.confidence != null && <Chip size="small" variant="outlined" label={`${Math.round(fact.confidence * 100)}% extraction confidence`} />}</Stack>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
      <Box>
        <SectionHeader title="Chronology" subtitle="Dates and events remain tied to the material that supports them." action={<Button size="small" startIcon={<AddRounded />} onClick={onAddEvent}>Add event</Button>} />
        {!matter.timeline.length ? <EmptyState icon={<EventRounded />} title="No timeline events" body="Add the incident, police contact, disclosure, appearances, and other material events." /> : (
          <Stack spacing={0}>
            {matter.timeline.map((event, index) => (
              <Stack key={event.id} direction="row" spacing={1.5} sx={{ minHeight: 92 }}>
                <Box sx={{ width: 88, pt: 0.25, textAlign: 'right' }}><Typography variant="caption" fontWeight={760}>{formatDate(event.eventDate)}</Typography></Box>
                <Box sx={{ width: 18, display: 'flex', flexDirection: 'column', alignItems: 'center' }}><Box sx={{ width: 10, height: 10, mt: 0.55, borderRadius: '50%', bgcolor: event.status === 'documented' ? 'secondary.main' : 'warning.main' }} />{index < matter.timeline.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider' }} />}</Box>
                <Box sx={{ flex: 1, pb: 2.2 }}><Typography sx={{ fontWeight: 720 }}>{event.title}</Typography>{event.description && <Typography variant="body2" color="text.secondary" sx={{ my: 0.25 }}>{event.description}</Typography>}<SourceLine item={event} /></Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function IssuesPanel({ matter, onAdd, onResearch, onRecordOutcome }) {
  return (
    <Box>
      <SectionHeader title="Issues and research plan" subtitle="Frame answerable questions before searching. Supporting and adverse authority both belong in the result." action={<Button variant="contained" startIcon={<AddRounded />} onClick={onAdd}>Add legal issue</Button>} />
      {!matter.issues.length ? <EmptyState icon={<BalanceRounded />} title="No legal issues framed" body="Add a legal question, its priority, and the potentially governing statute or rule." /> : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 1.4 }}>
          {matter.issues.map(issue => (
            <Paper key={issue.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" spacing={1}><Chip size="small" label={issue.priority} color={toneFor(issue.priority)} /><Stack direction="row" spacing={0.5}><Chip size="small" variant="outlined" label={issue.researchState} /><Chip size="small" color={issue.resolutionStatus === 'resolved' ? 'success' : 'default'} variant="outlined" label={issue.resolutionStatus || 'open'} /></Stack></Stack>
              <Typography variant="h6" sx={{ mt: 1.4 }}>{issue.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, minHeight: 42 }}>{issue.question || 'Research question not yet particularized.'}</Typography>
              {issue.governingLaw && <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>Potential authority: {issue.governingLaw}</Typography>}
              {issue.resolutionNote && <Alert severity="success" sx={{ mt: 1.2, py: 0.2 }}><Typography variant="caption">{issue.resolutionNote}</Typography></Alert>}
              <Stack direction="row" spacing={0.7} sx={{ mt: 1.5 }}><Button size="small" startIcon={<SearchRounded />} endIcon={<ArrowForwardRounded />} onClick={() => onResearch(issue)}>Research</Button><Button size="small" variant="outlined" onClick={() => onRecordOutcome(issue)}>Record outcome</Button></Stack>
            </Paper>
          ))}
        </Box>
      )}
      <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mt: 2.5 }}>Research results are limited to the connected, rights-authorized corpus. Every citation and pinpoint must be checked against the original judgment before use.</Alert>
    </Box>
  );
}

function WorkflowPanel({ matter, stages, taskStage, setTaskStage, onAdd, onStatus, onApprove }) {
  const tasks = matter.tasks.filter(task => task.stage === taskStage);
  const complete = tasks.filter(task => task.status === 'completed').length;
  return (
    <Box>
      <SectionHeader title="Lifecycle workflow" subtitle="One guided journey from intake to closure. Dates, filings, scope and strategic decisions still require professional verification." action={<Button startIcon={<AddRounded />} onClick={onAdd}>Add task</Button>} />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2.2 }}>
        <Select size="small" value={taskStage} onChange={event => setTaskStage(event.target.value)} sx={{ minWidth: 300 }}>{stages.map(stage => <MenuItem key={stage.id} value={stage.id}>{stage.title}</MenuItem>)}</Select>
        <Box sx={{ flex: 1 }}><Stack direction="row" justifyContent="space-between"><Typography variant="caption">Stage progress</Typography><Typography variant="caption" fontWeight={800}>{complete}/{tasks.length}</Typography></Stack><LinearProgress variant="determinate" value={tasks.length ? (complete / tasks.length) * 100 : 0} sx={{ mt: 0.7, height: 6, borderRadius: 8 }} /></Box>
      </Stack>
      <Stack spacing={1}>
        {tasks.map(task => (
          <Paper key={task.id} variant="outlined" sx={{ px: 1.25, py: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1}>
              <FormControlLabel sx={{ flex: 1, m: 0 }} control={<Checkbox checked={task.status === 'completed'} onChange={event => onStatus(task, event.target.checked)} />} label={<Box><Typography variant="body2" sx={{ fontWeight: task.status === 'completed' ? 500 : 680, textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'text.secondary' : 'text.primary' }}>{task.title}</Typography><Typography variant="caption" color="text.secondary">{task.dueDate ? `Due ${formatDate(task.dueDate)}` : 'Deadline not yet verified'} · {task.source}</Typography></Box>} />
              <Stack direction="row" spacing={0.7} alignItems="center">
                <Chip size="small" label={task.priority} color={toneFor(task.priority)} variant="outlined" />
                {task.requiresApproval && (task.approvedAt
                  ? <Chip size="small" icon={<CheckCircleRounded />} label={`Approved by ${task.approvedBy}`} color="success" />
                  : task.status === 'completed'
                    ? <Button size="small" variant="outlined" onClick={() => onApprove(task)}>Approve</Button>
                    : <Chip size="small" label="Approval after completion" variant="outlined" />)}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function AuditPanel({ audit, integrity }) {
  return (
    <Box>
      <SectionHeader title="Audit trail" subtitle="Material actions are appended to the matter record without copying confidential content into the log." />
      {integrity && <Alert severity={integrity.verified ? 'success' : 'error'} icon={integrity.verified ? <CheckCircleRounded /> : <WarningAmberRounded />} sx={{ mb: 2 }}>{integrity.verified ? `${integrity.totalEvents} events verified against the ${integrity.algorithm}.` : `Audit integrity failed at event ${integrity.firstBrokenEventId}. Stop and investigate before relying on this record.`}</Alert>}
      {!audit.length ? <EmptyState icon={<HistoryRounded />} title="No audit events" body="Matter changes, evidence access, AI processing, workflow updates, and approvals will appear here." /> : (
        <Stack spacing={0}>
          {audit.map((event, index) => (
            <Stack key={event.id} direction="row" spacing={1.5} sx={{ pb: index < audit.length - 1 ? 2 : 0 }}>
              <Box sx={{ width: 34, height: 34, flexShrink: 0, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'grey.50', color: 'text.secondary' }}><HistoryRounded sx={{ fontSize: 18 }} /></Box>
              <Box sx={{ flex: 1, pb: 1.6, borderBottom: index < audit.length - 1 ? '1px solid' : 0, borderColor: 'divider' }}><Stack direction="row" justifyContent="space-between" spacing={1}><Typography variant="body2" sx={{ fontWeight: 720 }}>{event.action}</Typography><Typography variant="caption" color="text.secondary">{formatDate(event.createdAt, true)}</Typography></Stack><Typography variant="caption" color="text.secondary">{event.actorName} · {event.actorRole} · {event.entityType}</Typography></Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function IssueResolutionDialog({ issue, onClose, onSave, saving }) {
  const [value, setValue] = useState({ resolutionStatus: 'investigating', researchState: 'in-progress', resolutionNote: '', resolutionBasis: { evidence: '', authorities: '', decision: '' } });
  useEffect(() => {
    if (!issue) return;
    setValue({
      resolutionStatus: issue.resolutionStatus || 'investigating',
      researchState: issue.researchState || 'in-progress',
      resolutionNote: issue.resolutionNote || '',
      resolutionBasis: {
        evidence: issue.resolutionBasis?.evidence || '',
        authorities: issue.resolutionBasis?.authorities || '',
        decision: issue.resolutionBasis?.decision || '',
      },
    });
  }, [issue]);
  const basis = key => event => setValue(current => ({ ...current, resolutionBasis: { ...current.resolutionBasis, [key]: event.target.value } }));
  return <Dialog open={Boolean(issue)} onClose={onClose} fullWidth maxWidth="sm"><DialogTitle>Record investigation outcome</DialogTitle><DialogContent dividers><Typography variant="subtitle1" sx={{ fontWeight: 750 }}>{issue?.title}</Typography><Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{issue?.question}</Typography><Stack spacing={2}><TextField select label="Investigation status" value={value.resolutionStatus} onChange={event => setValue(current => ({ ...current, resolutionStatus: event.target.value }))}>{['open', 'investigating', 'resolved', 'deferred'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}</TextField><TextField select label="Research state" value={value.researchState} onChange={event => setValue(current => ({ ...current, researchState: event.target.value }))}>{['not-started', 'in-progress', 'complete', 'requires-update'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}</TextField><TextField label="Outcome and reasoning" required={value.resolutionStatus === 'resolved'} multiline minRows={3} value={value.resolutionNote} onChange={event => setValue(current => ({ ...current, resolutionNote: event.target.value }))} helperText="Explain what was investigated, the conclusion reached, limitations, and any follow-up." /><TextField label="Evidence relied on" value={value.resolutionBasis.evidence} onChange={basis('evidence')} placeholder="Files and exact pages, paragraphs, or timestamps" /><TextField label="Authorities relied on" value={value.resolutionBasis.authorities} onChange={basis('authorities')} placeholder="Style of cause, citation, and pinpoint" /><TextField label="Professional decision / next step" value={value.resolutionBasis.decision} onChange={basis('decision')} /></Stack><Alert severity="info" sx={{ mt: 2 }}>Saving creates a dated audit event. A resolved issue requires a written outcome.</Alert></DialogContent><DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={() => onSave(value)} disabled={saving || (value.resolutionStatus === 'resolved' && !value.resolutionNote.trim())}>{saving ? 'Saving…' : 'Record outcome'}</Button></DialogActions></Dialog>;
}

function CreateMatterDialog({ open, value, setValue, onClose, onCreate, saving }) {
  const field = key => event => setValue(current => ({ ...current, [key]: event.target.value }));
  const setMatterType = event => {
    const matterType = event.target.value;
    setValue(current => ({ ...current, matterType, practiceArea: matterType === 'criminal' ? 'Criminal and regulatory' : 'Civil dispute' }));
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create a private matter</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>This creates the matter database record, private evidence vault, audit trail, and complete lifecycle checklist. It does not upload anything to OpenAI.</Alert>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2, pt: 0.5 }}>
          <TextField select label="Matter type" value={value.matterType} onChange={setMatterType}><MenuItem value="civil">Dispute / claim</MenuItem><MenuItem value="criminal">Criminal / regulatory</MenuItem></TextField>
          <TextField label="Matter title" required value={value.title} onChange={field('title')} placeholder={value.matterType === 'criminal' ? 'R. v. Client — charge' : 'Client v. Other Party — claim'} />
          <TextField label="Client name" value={value.clientName} onChange={field('clientName')} />
          <TextField label="Practice area" value={value.practiceArea} onChange={field('practiceArea')} />
          <TextField select label="Jurisdiction" value={value.jurisdiction} onChange={field('jurisdiction')}><MenuItem value="Ontario">Ontario</MenuItem><MenuItem value="British Columbia">British Columbia</MenuItem></TextField>
          <TextField label="Court / tribunal" value={value.court} onChange={field('court')} />
          <TextField label="File number" value={value.fileNumber} onChange={field('fileNumber')} />
          <TextField label="Next court date" type="date" InputLabelProps={{ shrink: true }} value={value.nextCourtDate} onChange={field('nextCourtDate')} />
          <TextField label="Objectives and initial instructions" multiline minRows={3} value={value.objectives} onChange={field('objectives')} sx={{ gridColumn: { sm: '1 / -1' } }} />
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={onCreate} disabled={saving || !value.title.trim()}>{saving ? 'Creating…' : 'Create matter'}</Button></DialogActions>
    </Dialog>
  );
}

function RecordDialog({ kind, value, setValue, matter, onClose, onCreate, saving }) {
  const field = key => event => setValue(current => ({ ...current, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const title = { fact: 'Add a sourced fact', timeline: 'Add a timeline event', issue: 'Add a legal issue', task: 'Add a workflow task' }[kind] || '';
  const evidenceFields = kind === 'fact' || kind === 'timeline';
  return (
    <Dialog open={Boolean(kind)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {kind === 'fact' && <TextField label="Fact statement" required multiline minRows={3} value={value.statement || ''} onChange={field('statement')} />}
          {kind === 'timeline' && <><TextField label="Event title" required value={value.title || ''} onChange={field('title')} /><TextField label="Event date" type="date" InputLabelProps={{ shrink: true }} value={value.eventDate || ''} onChange={field('eventDate')} /><TextField label="Description" multiline minRows={3} value={value.description || ''} onChange={field('description')} /></>}
          {kind === 'issue' && <><TextField label="Issue title" required value={value.title || ''} onChange={field('title')} /><TextField label="Answerable research question" multiline minRows={3} value={value.question || ''} onChange={field('question')} /><TextField label="Potential governing statute, rule, or test" value={value.governingLaw || ''} onChange={field('governingLaw')} /></>}
          {kind === 'task' && <><TextField label="Task" required value={value.title || ''} onChange={field('title')} /><TextField label="Due date" type="date" InputLabelProps={{ shrink: true }} value={value.dueDate || ''} onChange={field('dueDate')} /><FormControlLabel control={<Checkbox checked={Boolean(value.requiresApproval)} onChange={field('requiresApproval')} />} label="Require professional approval" /></>}
          {(kind === 'fact' || kind === 'timeline') && <TextField label="Evidence status" select value={value.status || 'reported'} onChange={field('status')}>{['reported', 'documented', 'inferred', 'requires-review'].map(status => <MenuItem key={status} value={status}>{status}</MenuItem>)}</TextField>}
          {(kind === 'issue' || kind === 'task') && <TextField label="Priority" select value={value.priority || 'normal'} onChange={field('priority')}>{['urgent', 'high', 'normal', 'low'].map(priority => <MenuItem key={priority} value={priority}>{priority}</MenuItem>)}</TextField>}
          {evidenceFields && <><TextField label="Evidence source" select value={value.sourceEvidenceId || ''} onChange={field('sourceEvidenceId')}><MenuItem value="">No evidence file linked</MenuItem>{(matter?.evidence || []).map(item => <MenuItem key={item.id} value={item.id}>{item.originalName}</MenuItem>)}</TextField><TextField label="Exact source locator" value={value.sourceLocator || ''} onChange={field('sourceLocator')} placeholder="Page 19, para 2 / audio 12:35 / message dated…" />{kind === 'fact' && <FormControlLabel control={<Checkbox checked={Boolean(value.disputed)} onChange={field('disputed')} color="error" />} label="This fact is disputed or conflicts with another source" />}</>}
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" onClick={onCreate} disabled={saving}>{saving ? 'Saving…' : 'Add to matter'}</Button></DialogActions>
    </Dialog>
  );
}

function ProcessingDialog({ processing, selectedCount, consent, setConsent, onClose, onRun, saving, matter }) {
  const analyzing = processing?.kind === 'analyze';
  const language = matterLanguage(matter || {});
  return (
    <Dialog open={Boolean(processing)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{analyzing ? `Run complete ${language.evidence} review` : 'Transcribe recording'}</DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" icon={<LockRounded />}>
          {analyzing
            ? `${selectedCount} selected file${selectedCount === 1 ? '' : 's'} will be sent to the configured OpenAI API for this review. Temporary API files are deleted after processing.`
            : 'This recording will be sent to the configured OpenAI transcription API. The transcript is saved as a derivative and the original remains unchanged.'}
        </Alert>
        {analyzing && <Box sx={{ mt: 2 }}><Typography variant="body2" sx={{ fontWeight: 750 }}>The review will create:</Typography><Typography variant="body2" color="text.secondary">A document and witness register; source-linked facts and chronology; contradiction and missing-material flags; supporting and adverse authorities; a preparation checklist; and a draft research memorandum.</Typography></Box>}
        <Typography variant="body2" sx={{ mt: 2 }}>Before proceeding, confirm that the firm has authority to process this confidential material with the configured provider and that any required client consent or contractual safeguards are in place.</Typography>
        <FormControlLabel sx={{ mt: 1.5, alignItems: 'flex-start' }} control={<Checkbox checked={consent} onChange={event => setConsent(event.target.checked)} />} label="I confirm this processing is authorized for this matter." />
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cancel</Button><Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeRounded />} onClick={onRun} disabled={!consent || saving || (analyzing && !selectedCount)}>{saving ? 'Processing…' : analyzing ? 'Review files' : 'Create transcript'}</Button></DialogActions>
    </Dialog>
  );
}
